import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_ENDPOINTS } from "../config/api";
import {
  FaYoutube,
  FaPlus,
  FaSave,
  FaTrash,
  FaCheckCircle,
  FaSearch,
  FaInfoCircle,
  FaHeading,
  FaLink
} from "react-icons/fa";
import "../styles/ManageLinks.css";

const API_URL = API_ENDPOINTS.SPIRITUAL_LINKS;

const ManageLinks = () => {
  /* ================= STATE ================= */
  const [header, setHeader] = useState({
    title: "Spiritual Video Library",
    subtitle: "Watch divine events, blessings & devotional celebrations",
  });
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Redesign Tab & Search states
  const [activeTab, setActiveTab] = useState("videos"); // "videos" | "header"
  const [searchQuery, setSearchQuery] = useState("");

  /* ================= FETCH ================= */
  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    try {
      setLoading(true);
      const res = await axios.get(API_URL);

      if (res.data) {
        if (res.data.header) {
          setHeader(res.data.header);
        }
        if (Array.isArray(res.data.videos)) {
          setVideos(res.data.videos);
        }
      }
    } catch (error) {
      console.log("FETCH ERROR:", error.response?.data || error.message);
    } finally {
      setLoading(false);
    }
  };

  /* ================= EXTRACT YOUTUBE VIDEO ID ================= */
  const extractYoutubeId = (url) => {
    if (!url) return "";

    if (url.length <= 15 && !url.includes("http")) {
      return url.trim();
    }

    if (url.includes("youtu.be/")) {
      return url.split("youtu.be/")[1]?.split("?")[0];
    }

    if (url.includes("watch?v=")) {
      return url.split("watch?v=")[1]?.split("&")[0];
    }

    if (url.includes("/embed/")) {
      return url.split("/embed/")[1]?.split("?")[0];
    }

    return url.trim();
  };

  /* ================= UPDATE VIDEO ================= */
  const updateVideo = (index, field, value) => {
    const updated = [...videos];

    if (field === "video_id") {
      updated[index][field] = extractYoutubeId(value);
    } else {
      updated[index][field] = value;
    }

    setVideos(updated);
  };

  /* ================= ADD VIDEO ================= */
  const addVideo = () => {
    setVideos([
      {
        title: "",
        video_id: "",
        description: "",
      },
      ...videos,
    ]);
    setActiveTab("videos");
  };

  /* ================= DELETE VIDEO ================= */
  const deleteVideo = (index) => {
    if (window.confirm("Are you sure you want to delete this spiritual video?")) {
      const updated = videos.filter((_, i) => i !== index);
      setVideos(updated);
    }
  };

  /* ================= SAVE ================= */
  const handleSave = async () => {
    try {
      setLoading(true);
      setSaveSuccess(false);

      const cleanedVideos = videos
        .filter((video) => video.title?.trim() && video.video_id?.trim())
        .map((video) => ({
          title: video.title,
          video_id: extractYoutubeId(video.video_id),
          description: video.description,
        }));

      const payload = {
        header,
        videos: cleanedVideos,
      };

      await axios.post(API_URL, payload, {
        headers: { "Content-Type": "application/json" },
      });

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      alert("🎉 Spiritual Links Updated Successfully!");
      fetchLinks();
    } catch (error) {
      console.log("SAVE ERROR:", error.response?.data || error.message);
      alert(error.response?.data?.message || "❌ Failed To Save Data");
    } finally {
      setLoading(false);
    }
  };

  /* ================= FILTER LOGIC ================= */
  const filteredVideos = videos.filter(video => 
    video.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    video.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="manage-links-container">
      {/* BANNER SECTION */}
      <div className="links-banner">
        <div className="banner-content">
          <h1>🎥 Manage Spiritual Library</h1>
          <p>Add, edit, and categorize devotional videos, event recordings, and darshan clips to show on the website.</p>
        </div>
        <div className="banner-stats">
          <div className="stat-pill">
            <FaYoutube className="icon" />
            <span><strong>{videos.length}</strong> Devotional Videos</span>
          </div>
        </div>
      </div>

      {/* DASHBOARD CONTROLS & TAB WRAPPER */}
      <div className="dashboard-controls">
        <div className="tabs-wrapper">
          <button 
            className={`tab-btn ${activeTab === "videos" ? "active" : ""}`}
            onClick={() => setActiveTab("videos")}
          >
            <FaYoutube /> Video Directory
          </button>
          <button 
            className={`tab-btn ${activeTab === "header" ? "active" : ""}`}
            onClick={() => setActiveTab("header")}
          >
            <FaInfoCircle /> Library Details
          </button>
        </div>

        {activeTab === "videos" && (
          <div className="search-filter-box">
            <div className="search-input-wrapper">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search videos by title..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        )}

        <div className="quick-action-buttons">
          {activeTab === "videos" && (
            <button className="action-btn add-btn-primary" onClick={addVideo}>
              <FaPlus /> Add Video
            </button>
          )}
          
          <button 
            className={`action-btn save-btn-primary ${loading ? "saving" : ""} ${saveSuccess ? "saved" : ""}`}
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="spinner-small"></div> Saving...
              </>
            ) : saveSuccess ? (
              <>
                <FaCheckCircle /> Saved!
              </>
            ) : (
              <>
                <FaSave /> Save Changes
              </>
            )}
          </button>
        </div>
      </div>

      {/* PANEL CONTAINER */}
      <div className="panel-container">
        
        {/* PANEL: LIBRARY DETAILS */}
        {activeTab === "header" && (
          <div className="panel-card fade-in">
            <div className="panel-card-header">
              <h3><FaInfoCircle className="title-icon" /> Video Library Details</h3>
              <p>Configure the header texts that appear at the top of the spiritual video library.</p>
            </div>
            
            <div className="panel-card-body">
              <div className="form-group">
                <label>Header Title</label>
                <input
                  type="text"
                  value={header.title || ""}
                  onChange={(e) => setHeader({ ...header, title: e.target.value })}
                  placeholder="e.g. Spiritual Video Library"
                />
              </div>

              <div className="form-group">
                <label>Header Description</label>
                <textarea
                  value={header.subtitle || ""}
                  onChange={(e) => setHeader({ ...header, subtitle: e.target.value })}
                  placeholder="Describe what devotional videos visitors can find..."
                  rows={4}
                />
              </div>
            </div>
          </div>
        )}

        {/* PANEL: VIDEOS DIRECTORY */}
        {activeTab === "videos" && (
          <div className="directory-section fade-in">
            {filteredVideos.length === 0 ? (
              <div className="empty-state">
                <FaYoutube className="empty-icon" />
                <h3>No Videos Found</h3>
                <p>{searchQuery ? "Try searching for a different keyword." : "Add a new YouTube video entry to publish on the website."}</p>
                <button className="action-btn add-btn-primary" onClick={addVideo}>
                  <FaPlus /> Add Video Now
                </button>
              </div>
            ) : (
              <div className="video-layout-grid">
                {filteredVideos.map((video, index) => (
                  <div className="video-entry-card" key={index}>
                    <div className="video-preview-container">
                      {video.video_id ? (
                        <iframe
                          src={`https://www.youtube.com/embed/${extractYoutubeId(video.video_id)}`}
                          title={video.title || "YouTube Video"}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          referrerPolicy="strict-origin-when-cross-origin"
                          allowFullScreen
                          className="video-iframe"
                        ></iframe>
                      ) : (
                        <div className="no-video-placeholder">
                          <FaYoutube className="icon" />
                          <span>Paste Video ID or Link Below</span>
                        </div>
                      )}
                      
                      <button className="video-delete-icon-btn" onClick={() => deleteVideo(index)}>
                        <FaTrash />
                      </button>
                    </div>

                    <div className="video-card-form">
                      <div className="form-group-compact">
                        <label><FaHeading className="label-icon" /> Video Title</label>
                        <input
                          type="text"
                          value={video.title || ""}
                          onChange={(e) => updateVideo(index, "title", e.target.value)}
                          placeholder="e.g. Mahasatsang Bhajan Celebrations"
                        />
                      </div>

                      <div className="form-group-compact">
                        <label><FaLink className="label-icon" /> YouTube URL / Video ID</label>
                        <input
                          type="text"
                          value={video.video_id || ""}
                          onChange={(e) => updateVideo(index, "video_id", e.target.value)}
                          placeholder="Paste link, e.g. https://www.youtube.com/watch?v=..."
                        />
                      </div>

                      <div className="form-group-compact">
                        <label>Description / Event Context</label>
                        <textarea
                          value={video.description || ""}
                          onChange={(e) => updateVideo(index, "description", e.target.value)}
                          placeholder="Describe the occasion, bhajan singers, or event summary..."
                          rows={3}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* FLOATING SAVE BAR */}
      <div className="floating-save-bar">
        <div className="save-bar-info">
          <span>Unsaved changes to video library will be discarded.</span>
        </div>
        <button 
          className={`action-btn save-btn-primary ${loading ? "saving" : ""} ${saveSuccess ? "saved" : ""}`}
          onClick={handleSave}
          disabled={loading}
        >
          {loading ? (
            <>
              <div className="spinner-small"></div> Saving...
            </>
          ) : saveSuccess ? (
            <>
              <FaCheckCircle /> Saved Successfully!
            </>
          ) : (
            <>
              <FaSave /> Save Changes
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ManageLinks;
